/**
 * JourneyBook — a small 3D book used as the open affordance for journey entries that expand into a
 * full-screen document viewer (renderings, drawing set, pre-design report, …). Styled as a bound
 * presentation portfolio: brand-teal board, inset hairline frame, wide-tracked wordmark, quiet
 * ALDER JAMES foot mark. Clicking calls onOpen.
 *
 * Props: label (cover wordmark), onOpen(), caption? (optional muted line beneath),
 *        downloadUrl? (optional — renders a tracked "Download PDF" link below the book so the
 *        client can bypass the flip viewer and grab the file directly)
 */
import { useState } from 'react'
import { J } from '../journeyUI.jsx'

export default function JourneyBook({ label = 'Open', onOpen, caption, aspect, pageCount, downloadUrl }) {
  const [hover, setHover] = useState(false)
  const [linkHover, setLinkHover] = useState(false)
  // Shape the book to the document it opens (aspect = width / height; >1 landscape, <1 portrait),
  // keeping the long edge a constant so every book reads at a similar scale.
  const LONG = 300
  const ar = aspect || (17 / 11)
  const W = ar >= 1 ? LONG : Math.round(LONG * ar)
  const H = ar >= 1 ? Math.round(LONG / ar) : LONG

  return (
    <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center' }}>
      <button
        type="button"
        onClick={onOpen}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', perspective: '1600px', display: 'inline-block' }}
        title="Open"
      >
        <div style={{
          position: 'relative', width: W, height: H, transformStyle: 'preserve-3d',
          transform: hover ? 'rotateY(-9deg) translateY(-3px)' : 'rotateY(-16deg)',
          transition: 'transform 0.45s cubic-bezier(0.22,1,0.36,1), filter 0.45s ease',
          filter: hover
            ? 'drop-shadow(0 30px 46px rgba(4,21,24,0.34))'
            : 'drop-shadow(0 22px 36px rgba(4,21,24,0.28))',
        }}>
          {/* page-block behind (warm paper), hinting thickness */}
          <div style={{ position: 'absolute', top: 4, left: 6, width: W, height: H, background: '#dcdad4', borderRadius: '1px 4px 4px 1px', transform: 'translateZ(-8px)' }} />
          <div style={{ position: 'absolute', top: 2, left: 3, width: W, height: H, background: '#f2f0ec', borderRadius: '1px 4px 4px 1px', transform: 'translateZ(-4px)' }} />
          {/* cover face — brand-teal board, tracked wordmark, inset hairline frame */}
          <div style={{
            position: 'absolute', inset: 0, borderRadius: '1px 5px 5px 1px', overflow: 'hidden',
            background: 'linear-gradient(155deg, #10333a 0%, #07272D 62%, #05222a 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 30px', textAlign: 'center',
          }}>
            {/* debossed hairline frame */}
            <div style={{ position: 'absolute', inset: 11, border: '0.5px solid rgba(255,255,255,0.16)', borderRadius: 1, pointerEvents: 'none' }} />
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
              {/* paddingLeft compensates the trailing tracking space so the word sits optically centered */}
              <div style={{ fontFamily: '"Montserrat",sans-serif', fontSize: 14, fontWeight: 300, letterSpacing: '0.34em', paddingLeft: '0.34em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.9)', lineHeight: 1.7 }}>{label}</div>
              {pageCount != null && (
                <div style={{ fontFamily: '"DM Mono", monospace', fontSize: 9, letterSpacing: '0.2em', paddingLeft: '0.2em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.36)' }}>{pageCount} pages</div>
              )}
            </div>
            {/* firm mark at the foot of the board */}
            <div style={{ position: 'absolute', bottom: 22, left: 0, right: 0, textAlign: 'center', fontFamily: '"Montserrat",sans-serif', fontSize: 6.5, fontWeight: 500, letterSpacing: '0.44em', paddingLeft: '0.44em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.26)', pointerEvents: 'none' }}>
              Alder James
            </div>
            {/* binding (spine) */}
            <div style={{ position: 'absolute', top: 0, left: 0, bottom: 0, width: 16, background: 'linear-gradient(90deg, rgba(0,0,0,0.4), rgba(0,0,0,0.1) 55%, rgba(0,0,0,0))' }} />
            {/* soft sheen */}
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(105deg, rgba(255,255,255,0.09), rgba(255,255,255,0) 46%)' }} />
          </div>
        </div>
      </button>
      {caption && <div style={{ ...J.mono, color: '#7a9aaa', marginTop: 16, textAlign: 'center' }}>{caption}</div>}
      {downloadUrl && (
        <a
          href={downloadUrl} target="_blank" rel="noopener noreferrer"
          onMouseEnter={() => setLinkHover(true)}
          onMouseLeave={() => setLinkHover(false)}
          style={{
            ...J.textLink,
            marginTop: caption ? 10 : 18,
            color: linkHover ? '#07272D' : '#6b8f95',
            borderBottomColor: linkHover ? 'rgba(7,39,45,0.5)' : 'rgba(7,39,45,0.22)',
          }}
        >
          Download PDF
        </a>
      )}
    </div>
  )
}
